import React from 'react';
import { Upload, Download, FileText } from 'lucide-react';
import { useTranslation } from '../../../contexts/TranslationContext';
import { Lesson } from '../../../types';
import toast from 'react-hot-toast';

interface ImportExportSectionProps {
  formData: Partial<Lesson>;
  setFormData: React.Dispatch<React.SetStateAction<Partial<Lesson>>>;
  importing: boolean;
  setImporting: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ImportExportSection: React.FC<ImportExportSectionProps> = ({
  formData,
  setFormData,
  importing,
  setImporting
}) => {
  const { t } = useTranslation();
  const handleImportLesson = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importedData = JSON.parse(e.target?.result as string);
        
        // Validate the imported data structure
        if (!importedData.metadata || !importedData.objectives || !importedData.timeline) {
          throw new Error('Invalid lesson format');
        }

        // Clear the lesson_id so Firebase generates a new one
        const lessonData = {
          ...importedData,
          metadata: {
            ...importedData.metadata,
            lesson_id: ''
          }
        };

        setFormData(lessonData);
        setImporting(false);
        
        // Show success message
        toast.success(t('importExport.lessonImported'));
      } catch (error) {
        console.error('Error importing lesson:', error);
        setImporting(false);
        
        // Show error message
        toast.error(t('importExport.importFailed'));
      }
    };
    reader.readAsText(file);
  };

  const handleExportLesson = () => {
    const lessonData = JSON.stringify(formData, null, 2);
    const blob = new Blob([lessonData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${formData.metadata?.title || 'lesson'}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleExportSampleLesson = () => {
    const sampleLesson: Lesson = {
      metadata: {
        lesson_id: '',
        title: 'Water Cycle Lesson',
        subject: 'Science',
        grade: '3',
        language: 'en',
        version: '1.0.0',
        duration_minutes: 45,
        created_at: new Date().toISOString().split('T')[0]
      },
      objectives: [
        'Understand the water cycle process',
        'Identify the four main stages of the water cycle',
        'Explain how water moves through the environment'
      ],
      success_criteria: [
        'I can name the four stages of the water cycle',
        'I can explain what happens in each stage',
        'I can draw a simple water cycle diagram'
      ],
      standards_alignment: [
        'NGSS.3-ESS2-1',
        'CCSS.ELA-LITERACY.RI.3.3'
      ],
      prerequisites: 'Basic understanding of states of matter (solid, liquid, gas)',
      materials: {
        digital: [
          'Interactive water cycle diagram',
          'Virtual lab simulation',
          'Digital assessment tools'
        ],
        physical_alternatives: [
          'Printed water cycle diagrams',
          'Clear containers for demonstration',
          'Ice cubes and warm water'
        ]
      },
      resources: {
        water_cycle_diagram: {
          type: 'interactive_diagram',
          stages: ['evaporation', 'condensation', 'precipitation', 'collection'],
          stage_data: {
            evaporation: {
              icon: '💧',
              title: 'Evaporation',
              description: 'Water turns into vapor',
              details: 'Heat from the sun causes water to change from liquid to gas'
            },
            condensation: {
              icon: '☁️',
              title: 'Condensation',
              description: 'Vapor forms clouds',
              details: 'Water vapor cools and forms tiny water droplets'
            },
            precipitation: {
              icon: '🌧️',
              title: 'Precipitation',
              description: 'Water falls to Earth',
              details: 'Water droplets become heavy and fall as rain, snow, or hail'
            },
            collection: {
              icon: '🌊',
              title: 'Collection',
              description: 'Water gathers in bodies',
              details: 'Water collects in oceans, lakes, rivers, and underground'
            }
          },
          interactive: true,
          clickable: true
        },
        virtual_lab: {
          type: 'simulation',
          components: ['beaker', 'heat_source', 'condenser'],
          component_info: {
            beaker: { icon: '🧪', name: 'Water Container' },
            heat_source: { icon: '🔥', name: 'Heat Source' },
            condenser: { icon: '❄️', name: 'Cooling System' }
          },
          simulation_data: {
            title: 'Water Cycle Experiment',
            description: 'Observe the water cycle in action',
            initial_conditions: { temperature: 25, humidity: 60 },
            steps: [
              { step: 1, status: 'Heating water', conditions: { temperature: 50 } },
              { step: 2, status: 'Water evaporating', conditions: { temperature: 75, humidity: 80 } },
              { step: 3, status: 'Condensation forming', conditions: { temperature: 60, humidity: 90 } }
            ]
          },
          interactive: true
        }
      },
      timeline: [
        {
          id: 'introduction',
          name: 'Introduction to Water Cycle',
          minutes: 10,
          step_type: 'launch',
          teacher_script: [
            'Today we will learn about the water cycle',
            'The water cycle is how water moves around our planet',
            'Let\'s start by looking at our interactive diagram'
          ],
          student_actions: [
            'Listen to the introduction',
            'Look at the water cycle diagram',
            'Ask questions about what they see'
          ],
          checks_for_understanding: [],
          resources: [{ resourceId: 'water_cycle_diagram' }]
        }
      ],
      assessments: {
        water_cycle_quiz: {
          items: [
            {
              id: 'WC1',
              type: 'multiple_choice',
              prompt: 'Which stage of the water cycle turns liquid water into water vapor?',
              choices: ['Evaporation', 'Condensation', 'Precipitation', 'Collection'],
              answer: 0
            }
          ],
          scoring: { correct_per_item: 1, max: 5, mastery_threshold: 4 }
        }
      },
      misconceptions_and_responses: [
        {
          misconception: 'Water disappears during evaporation',
          response: 'Explain that water changes form but doesn\'t disappear - it becomes invisible water vapor.'
        }
      ],
      differentiation: {
        supports: {
          visual_aids: 'Use pictures and diagrams for each stage',
          hands_on: 'Provide physical models to manipulate'
        },
        extensions: {
          research: 'Research local water cycle patterns',
          presentation: 'Create a water cycle presentation'
        },
        ell: {
          vocabulary: ['evaporation', 'condensation', 'precipitation', 'collection'],
          visuals: ['Use pictures for each vocabulary word']
        }
      },
      classroom_management_notes: [
        'Supervise carefully around heat sources',
        'Have students work in small groups',
        'Keep water containers away from electronics'
      ],

      data_and_reporting: {
        events: [
          {
            when: 'experiment_complete',
            capture: ['student_id', 'observations', 'understanding_level']
          }
        ],
        next_lesson_grouping_rule: 'Based on experiment observations, group for follow-up activities.'
      },
      offline_variant: {
        directions: [
          'Use printed diagrams and physical materials',
          'Teacher demonstrates the experiment',
          'Students draw and label the water cycle'
        ]
      },
      home_connection: {
        family_note: 'Observe the water cycle in action: watch for rain, clouds, and puddles. Ask: Where does the water go?'
      }
    };

    const lessonData = JSON.stringify(sampleLesson, null, 2);
    const blob = new Blob([lessonData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample_water_cycle_lesson.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="card">
      <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
        <FileText className="w-5 h-5 mr-2" />
        {t('importExport.importExport')}
      </h2>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Import Lesson */}
        <div className="space-y-3">
          <h3 className="font-medium text-gray-900">{t('importExport.importLesson')}</h3>
          <p className="text-sm text-gray-600">
            {t('importExport.importDescription')}
          </p>
          <label className="btn-secondary flex items-center space-x-2 cursor-pointer">
            <Upload className="w-4 h-4" />
            <span>{importing ? t('importExport.importButton') + '...' : t('importExport.importButton')}</span>
            <input
              type="file"
              accept="*.json"
              onChange={handleImportLesson}
              className="hidden"
              disabled={importing}
            />
          </label>
        </div>

        {/* Export Current Lesson */}
        <div className="space-y-3">
          <h3 className="font-medium text-gray-900">{t('importExport.exportLesson')}</h3>
          <p className="text-sm text-gray-600">
            {t('importExport.exportDescription')}
          </p>
          <button
            type="button"
            onClick={handleExportLesson}
            className="btn-secondary flex items-center space-x-2"
          >
            <Download className="w-4 h-4" />
            <span>{t('importExport.exportLesson')}</span>
          </button>
        </div>

        {/* Export Sample Lesson */}
        <div className="space-y-3">
          <h3 className="font-medium text-gray-900">{t('importExport.sampleLesson')}</h3>
          <p className="text-sm text-gray-600">
            {t('importExport.sampleLessonDescription')}
          </p>
          <button
            type="button"
            onClick={handleExportSampleLesson}
            className="btn-secondary flex items-center space-x-2"
          >
            <Download className="w-4 h-4" />
            <span>{t('importExport.downloadSample')}</span>
          </button>
        </div>
      </div>

      <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
        <h4 className="font-medium text-blue-900 mb-2">{t('importExport.importExportInformation')}</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• {t('importExport.importInfo1')}</li>
          <li>• {t('importExport.importInfo2')}</li>
          <li>• {t('importExport.importInfo3')}</li>
          <li>• {t('importExport.importInfo4')}</li>
        </ul>
      </div>
    </div>
  );
};
